/**
 * FileBrowser Component
 * Main container for the file portal with folder tree, search, multi-select, favourites, and file display
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import FolderTree from './FolderTree';
import Breadcrumb from './Breadcrumb';
import SearchBar from './SearchBar';
import FileCard from './FileCard';
import FileRow from './FileRow';
import NewFilesModal from './NewFilesModal';
import AssetEditorBar from './AssetEditorBar';
import AssetSelectionActions from './AssetSelectionActions';
import { fetchCanEdit, fetchObsoleteAssets } from './assetEditorApi';
import { readApiError } from '../../utils/api-error';

const FileBrowser = () => {
  // State
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [stats, setStats] = useState({ folders: 0, files: 0 });

  // Multi-select state
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [isDownloadingSelected, setIsDownloadingSelected] = useState(false);

  // Favourites state
  const [favourites, setFavourites] = useState({ files: [], folders: [] });
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);

  // New files modal state
  const [showNewFilesModal, setShowNewFilesModal] = useState(false);
  const [newFiles, setNewFiles] = useState([]);
  const [newFilesTotal, setNewFilesTotal] = useState(0);
  const [hasCheckedNewFiles, setHasCheckedNewFiles] = useState(false);

  // Reseller Assets editing (permission granted per user by an admin)
  const [canEdit, setCanEdit] = useState(false);
  const [treeRefreshKey, setTreeRefreshKey] = useState(0);

  // Obsolete files (superseded, kept for older machines) are hidden by default
  const [obsoleteMap, setObsoleteMap] = useState({});
  const [showObsolete, setShowObsolete] = useState(() => {
    try { return localStorage.getItem('rfShowObsolete') === '1'; } catch (e) { return false; }
  });

  const fetchObsolete = useCallback(() => {
    fetchObsoleteAssets()
      .then(setObsoleteMap)
      .catch((err) => console.error('Obsolete list failed:', err));
  }, []);

  useEffect(() => { fetchObsolete(); }, [fetchObsolete]);

  const toggleShowObsolete = () => {
    setShowObsolete((value) => {
      try { localStorage.setItem('rfShowObsolete', value ? '0' : '1'); } catch (e) { /* private mode */ }
      return !value;
    });
    setSelectedFiles(new Set());
  };

  // Current files first, then obsolete ones, each carrying its marker
  const allFiles = useMemo(() => {
    const annotated = files.map(f => {
      const marker = obsoleteMap[String(f.id)];
      return marker ? { ...f, obsolete: marker } : f;
    });
    return annotated.filter(f => !f.obsolete).concat(annotated.filter(f => f.obsolete));
  }, [files, obsoleteMap]);
  const obsoleteCount = allFiles.filter(f => f.obsolete).length;
  const visibleFiles = showObsolete ? allFiles : allFiles.filter(f => !f.obsolete);

  useEffect(() => {
    fetchCanEdit()
      .then(setCanEdit)
      .catch((err) => console.error('Edit permission check failed:', err));
  }, []);

  // Fetch breadcrumb path
  const fetchBreadcrumb = useCallback(async (folderId) => {
    if (!folderId) {
      setBreadcrumbPath([]);
      return;
    }

    try {
      const response = await fetch(`/api/breadcrumb/${folderId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.breadcrumb) {
          setBreadcrumbPath(data.breadcrumb);
        }
      }
    } catch (err) {
      console.error('Breadcrumb fetch error:', err);
    }
  }, []);

  // Fetch folder contents
  const fetchContents = useCallback(async (folderId, search = '') => {
    try {
      setLoading(true);
      setError(null);
      setSelectedFiles(new Set()); // Clear selection on navigation

      const authToken = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${authToken}` };

      // Build query params
      const params = new URLSearchParams();
      if (search) params.set('search', search);

      const folderPath = folderId ? `/api/folders/${folderId}` : '/api/folders';
      const filePath = folderId ? `/api/files/${folderId}` : '/api/files';

      // Fetch folders and files in parallel
      const [foldersRes, filesRes] = await Promise.all([
        fetch(`${folderPath}?${params}`, { headers }),
        fetch(`${filePath}?${params}`, { headers })
      ]);

      if (!foldersRes.ok || !filesRes.ok) {
        throw new Error('Failed to load content');
      }

      const [foldersData, filesData] = await Promise.all([
        foldersRes.json(),
        filesRes.json()
      ]);

      const fetchedFolders = foldersData.success ? (foldersData.folders || []) : [];
      const fetchedFiles = filesData.success ? (filesData.files || []) : [];

      setFolders(fetchedFolders);
      setFiles(fetchedFiles);
      setStats({
        folders: fetchedFolders.length,
        files: fetchedFiles.length
      });
    } catch (err) {
      setError(err.message);
      console.error('Content fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user favourites
  const fetchFavourites = useCallback(async () => {
    try {
      const response = await fetch('/api/favourites', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.favourites) {
          setFavourites(data.favourites);
        }
      }
    } catch (err) {
      console.error('Favourites fetch error:', err);
    }
  }, []);

  // Fetch new files since last login
  const fetchNewFiles = useCallback(async () => {
    try {
      // Get the last login time from localStorage
      const lastLoginTime = localStorage.getItem('lastFileViewTime');
      const since = lastLoginTime ? parseInt(lastLoginTime, 10) : 0;

      // Skip if this is the user's first time (no previous login time)
      if (!lastLoginTime) {
        // Set the current time as the first login time
        localStorage.setItem('lastFileViewTime', Date.now().toString());
        setHasCheckedNewFiles(true);
        return;
      }

      const response = await fetch(`/api/files/new?since=${since}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.files && data.files.length > 0) {
          setNewFiles(data.files);
          setNewFilesTotal(data.total);
          setShowNewFilesModal(true);
        }
      }
    } catch (err) {
      console.error('New files fetch error:', err);
    } finally {
      setHasCheckedNewFiles(true);
    }
  }, []);

  // Handle closing the new files modal and updating the last view time
  const handleCloseNewFilesModal = useCallback(() => {
    setShowNewFilesModal(false);
    // Update the last view time so we don't show these files again
    localStorage.setItem('lastFileViewTime', Date.now().toString());
  }, []);

  // Handle navigating to a file's folder from the modal
  const handleNavigateToFile = useCallback((folderId) => {
    if (folderId) {
      setCurrentFolderId(folderId);
    }
  }, []);

  // Toggle favourite status
  const handleToggleFavourite = useCallback(async (itemId, itemType) => {
    try {
      const response = await fetch('/api/favourites/toggle', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ itemId, itemType })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.favourites) {
          setFavourites(data.favourites);
        }
      }
    } catch (err) {
      console.error('Toggle favourite error:', err);
    }
  }, []);

  // Check if item is a favourite
  const isItemFavourite = useCallback((itemId, itemType) => {
    if (itemType === 'folder') {
      return favourites.folders.includes(itemId);
    }
    return favourites.files.includes(itemId);
  }, [favourites]);

  // Initial load and folder navigation
  useEffect(() => {
    fetchContents(currentFolderId, searchQuery);
    fetchBreadcrumb(currentFolderId);
    fetchFavourites();
  }, [currentFolderId, fetchContents, fetchBreadcrumb, fetchFavourites]);

  // Check for new files on initial mount only
  useEffect(() => {
    if (!hasCheckedNewFiles) {
      fetchNewFiles();
    }
  }, [hasCheckedNewFiles, fetchNewFiles]);

  // Handle search
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    fetchContents(currentFolderId, query);
  }, [currentFolderId, fetchContents]);

  // Handle folder selection from tree or card
  const handleFolderSelect = (folderId, folderName) => {
    setCurrentFolderId(folderId);
    setSearchQuery('');
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbNavigate = (folderId) => {
    setCurrentFolderId(folderId);
    setSearchQuery('');
  };

  // Handle file selection toggle
  const handleFileSelect = (fileId) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // Handle select all files
  const handleSelectAll = () => {
    if (selectedFiles.size === visibleFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(visibleFiles.map(f => f.id)));
    }
  };

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectedFiles(new Set());
  };

  // Reload the current folder and the sidebar tree after an edit
  const handleAssetsChanged = () => {
    fetchContents(currentFolderId, searchQuery);
    fetchObsolete();
    setTreeRefreshKey((key) => key + 1);
  };

  const handleCurrentFolderDeleted = () => {
    const parent = breadcrumbPath.length > 1 ? breadcrumbPath[breadcrumbPath.length - 2].id : null;
    setTreeRefreshKey((key) => key + 1);
    setCurrentFolderId(parent);
  };

  // Handle single file download with correct extension
  const handleDownload = async (fileId, fileName, extension) => {
    try {
      const authToken = localStorage.getItem('authToken');

      const response = await fetch(`/api/download/${fileId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        throw new Error(await readApiError(response));
      }

      // Get the blob
      const blob = await response.blob();

      // Ensure filename has correct extension
      let downloadName = fileName;
      if (extension && !fileName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) {
        downloadName = `${fileName}.${extension}`;
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert(`Failed to download file: ${err.message}`);
    }
  };

  // Handle download selected files
  const handleDownloadSelected = async () => {
    if (selectedFiles.size === 0) return;

    setIsDownloadingSelected(true);

    try {
      const selectedFileList = files.filter(f => selectedFiles.has(f.id));

      // Download files sequentially with small delay to prevent browser blocking
      for (let i = 0; i < selectedFileList.length; i++) {
        const file = selectedFileList[i];
        await handleDownload(file.id, file.name, file.extension);

        // Small delay between downloads to prevent browser from blocking
        if (i < selectedFileList.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    } catch (err) {
      console.error('Batch download error:', err);
    } finally {
      setIsDownloadingSelected(false);
    }
  };

  // Render selection toolbar
  const renderSelectionToolbar = () => {
    if (visibleFiles.length === 0) return null;

    const allSelected = selectedFiles.size === visibleFiles.length && visibleFiles.length > 0;
    const someSelected = selectedFiles.size > 0;

    return (
      <div className="rf-selection-toolbar">
        <div className="rf-selection-checkbox">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={handleSelectAll}
            id="select-all"
            ref={(el) => {
              if (el) {
                el.indeterminate = someSelected && !allSelected;
              }
            }}
          />
          <label htmlFor="select-all">
            {allSelected ? 'Deselect All' : 'Select All'}
          </label>
        </div>

        {someSelected && (
          <div className="rf-selection-actions">
            <span className="rf-selection-count">
              {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''} selected
            </span>
            <button
              className="rf-btn rf-btn-primary rf-btn-sm"
              onClick={handleDownloadSelected}
              disabled={isDownloadingSelected}
            >
              {isDownloadingSelected ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Downloading...
                </>
              ) : (
                <>
                  <i className="fas fa-download"></i>
                  Download Selected
                </>
              )}
            </button>
            {canEdit && (
              <AssetSelectionActions
                selectedFiles={allFiles.filter(f => selectedFiles.has(f.id))}
                folderFiles={allFiles}
                onChanged={handleAssetsChanged}
              />
            )}
            <button
              className="rf-btn rf-btn-secondary rf-btn-sm"
              onClick={handleClearSelection}
            >
              <i className="fas fa-times"></i>
              Clear
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render content based on view mode
  const renderContent = () => {
    if (loading) {
      return (
        <div className="rf-loading">
          <div className="rf-loading-spinner"></div>
          <p className="rf-loading-text">Loading files...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="rf-empty-state">
          <i className="rf-empty-state-icon fas fa-exclamation-triangle"></i>
          <h3 className="rf-empty-state-title">Error Loading Files</h3>
          <p className="rf-empty-state-text">{error}</p>
          <button
            className="rf-btn rf-btn-primary"
            onClick={() => fetchContents(currentFolderId, searchQuery)}
            style={{ marginTop: '16px' }}
          >
            <i className="fas fa-redo"></i> Try Again
          </button>
        </div>
      );
    }

    if (folders.length === 0 && files.length === 0) {
      return (
        <div className="rf-empty-state">
          <i className="rf-empty-state-icon fas fa-folder-open"></i>
          <h3 className="rf-empty-state-title">
            {searchQuery ? 'No Results Found' : 'This Folder is Empty'}
          </h3>
          <p className="rf-empty-state-text">
            {searchQuery
              ? `No files or folders match "${searchQuery}"`
              : 'There are no files or subfolders in this location'}
          </p>
        </div>
      );
    }

    const FileComponent = viewMode === 'grid' ? FileCard : FileRow;
    const containerClass = viewMode === 'grid' ? 'rf-file-grid' : 'rf-file-list';

    // Filter by favourites if enabled
    const displayFolders = showFavouritesOnly
      ? folders.filter(folder => isItemFavourite(folder.id, 'folder'))
      : folders;
    const displayFiles = showFavouritesOnly
      ? visibleFiles.filter(file => isItemFavourite(file.id, 'file'))
      : visibleFiles;

    // Everything here is obsolete and hidden
    if (!showFavouritesOnly && displayFolders.length === 0 && displayFiles.length === 0) {
      return (
        <div className="rf-empty-state">
          <i className="rf-empty-state-icon fas fa-archive"></i>
          <h3 className="rf-empty-state-title">Only Obsolete Files Here</h3>
          <p className="rf-empty-state-text">
            {obsoleteCount} older file{obsoleteCount !== 1 ? 's are' : ' is'} kept here for customers who still need {obsoleteCount !== 1 ? 'them' : 'it'}.
          </p>
          <button className="rf-btn rf-btn-secondary" onClick={toggleShowObsolete} style={{ marginTop: '16px' }}>
            <i className="fas fa-eye"></i> Show obsolete files
          </button>
        </div>
      );
    }

    // Show message when favourites filter is active but nothing matches
    if (showFavouritesOnly && displayFolders.length === 0 && displayFiles.length === 0) {
      return (
        <div className="rf-empty-state">
          <i className="rf-empty-state-icon fas fa-star"></i>
          <h3 className="rf-empty-state-title">No Favourites Here</h3>
          <p className="rf-empty-state-text">
            {folders.length > 0 || files.length > 0
              ? 'None of the items in this folder are marked as favourites'
              : 'This folder is empty'}
          </p>
          <button
            className="rf-btn rf-btn-secondary"
            onClick={() => setShowFavouritesOnly(false)}
            style={{ marginTop: '16px' }}
          >
            <i className="fas fa-times"></i> Clear Filter
          </button>
        </div>
      );
    }

    return (
      <>
        {renderSelectionToolbar()}
        <div className={containerClass}>
          {/* Folders first */}
          {displayFolders.map((folder) => (
            <FileComponent
              key={`folder-${folder.id}`}
              file={folder}
              isFolder={true}
              onClick={handleFolderSelect}
              onDownload={handleDownload}
              isSelected={false}
              onSelect={() => {}}
              selectionMode={selectedFiles.size > 0}
              isFavourite={isItemFavourite(folder.id, 'folder')}
              onToggleFavourite={handleToggleFavourite}
            />
          ))}

          {/* Then files */}
          {displayFiles.map((file) => (
            <FileComponent
              key={`file-${file.id}`}
              file={file}
              isFolder={false}
              onClick={() => {}}
              onDownload={handleDownload}
              isSelected={selectedFiles.has(file.id)}
              onSelect={handleFileSelect}
              selectionMode={selectedFiles.size > 0}
              isFavourite={isItemFavourite(file.id, 'file')}
              onToggleFavourite={handleToggleFavourite}
              onDownloadCurrent={(id, name) => handleDownload(id, name, '')}
            />
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="rf-portal">
      {/* Sidebar with folder tree */}
      <aside className="rf-portal-sidebar">
        <FolderTree
          currentFolderId={currentFolderId}
          onFolderSelect={handleFolderSelect}
          refreshKey={treeRefreshKey}
        />
      </aside>

      {/* Main content area */}
      <main className="rf-portal-main">
        <header className="rf-portal-header">
          <Breadcrumb
            path={breadcrumbPath}
            onNavigate={handleBreadcrumbNavigate}
            rootLabel="Reseller Assets"
          />

          <div className="rf-search-row">
            <SearchBar
              onSearch={handleSearch}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />

            {/* Favourites filter button */}
            <button
              className={`rf-btn rf-btn-favourites ${showFavouritesOnly ? 'active' : ''}`}
              onClick={() => setShowFavouritesOnly(!showFavouritesOnly)}
              title={showFavouritesOnly ? 'Show all items' : 'Show favourites only'}
            >
              <i className={`${showFavouritesOnly ? 'fas' : 'far'} fa-star`}></i>
              <span className="rf-btn-favourites-label">
                {showFavouritesOnly ? 'Showing Favourites' : 'Favourites'}
              </span>
              {(favourites.files.length + favourites.folders.length) > 0 && (
                <span className="rf-btn-favourites-count">
                  {favourites.files.length + favourites.folders.length}
                </span>
              )}
            </button>

            {obsoleteCount > 0 && (
              <button
                className={`rf-btn rf-btn-favourites rf-btn-obsolete ${showObsolete ? 'active' : ''}`}
                onClick={toggleShowObsolete}
                title={showObsolete
                  ? 'Hide obsolete files'
                  : 'Show older files kept for customers who still need them (e.g. older machines)'}
              >
                <i className="fas fa-archive"></i>
                <span className="rf-btn-favourites-label">
                  {showObsolete ? 'Hide obsolete' : 'Show obsolete'}
                </span>
                <span className="rf-btn-favourites-count">{obsoleteCount}</span>
              </button>
            )}
          </div>

          {canEdit && (
            <AssetEditorBar
              currentFolderId={currentFolderId}
              currentFolderName={breadcrumbPath.length ? breadcrumbPath[breadcrumbPath.length - 1].name : ''}
              isEmpty={!loading && !error && !searchQuery && folders.length === 0 && files.length === 0}
              onChanged={handleAssetsChanged}
              onFolderDeleted={handleCurrentFolderDeleted}
            />
          )}

          {!loading && !error && (folders.length > 0 || files.length > 0) && (
            <div className="rf-folder-stats">
              {stats.folders > 0 && (
                <span className="rf-folder-stat">
                  <i className="fas fa-folder"></i>
                  {stats.folders} folder{stats.folders !== 1 ? 's' : ''}
                </span>
              )}
              {stats.files > 0 && (
                <span className="rf-folder-stat">
                  <i className="fas fa-file"></i>
                  {stats.files} file{stats.files !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
        </header>

        <div className="rf-portal-content">
          {renderContent()}
        </div>
      </main>

      {/* New Files Modal */}
      <NewFilesModal
        isOpen={showNewFilesModal}
        onClose={handleCloseNewFilesModal}
        files={newFiles}
        totalCount={newFilesTotal}
        onDownload={handleDownload}
        onNavigateToFile={handleNavigateToFile}
      />
    </div>
  );
};

export default FileBrowser;
